// src/lib/api/core/clients.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId, getValidatedUserId } from '../shared/helpers'
import { withErrorHandling, createSuccessResponse } from '../shared/errors'
import { validateRequired, validateEmail, sanitizeInput } from '../shared/validation'
import { logger } from '../../logger'

// PostgREST caps a single response at 1000 rows by default.
const CLIENTS_PAGE_SIZE = 1000

/**
 * Get all clients for a company
 * Migrated from supabaseApi.js lines 592-614
 */
const _getClients = async (companyId = null) => {
  logger.supabaseApi('getClients called with companyId:', companyId)
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    logger.supabaseApi('No company ID for getClients')
    return createSuccessResponse([])
  }

  logger.supabaseApi('Fetching clients for company:', resolvedCompanyId)
  // Page through every client. A fixed .limit(500) silently truncated larger
  // books, and every count/search in the UI ran over the truncated set.
  const clients = []
  for (let from = 0; ; from += CLIENTS_PAGE_SIZE) {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('company_id', resolvedCompanyId)
      .order('created_at', { ascending: false })
      .order('id', { ascending: true })
      .range(from, from + CLIENTS_PAGE_SIZE - 1)

    if (error) {
      logger.error('[supabaseApi] Error in getClients query:', error)
      throw error
    }

    clients.push(...(data || []))
    if (!data || data.length < CLIENTS_PAGE_SIZE) break
  }

  logger.supabaseApi('getClients success, found', clients.length, 'clients')
  return createSuccessResponse(clients)
}

/**
 * Create a new client
 * Migrated from supabaseApi.js lines 616-636
 */
const _createClient = async (clientData) => {
  // Validation
  validateRequired(clientData.name, 'Client name')
  if (clientData.email) {
    validateEmail(clientData.email)
  }
  
  const companyId = clientData.company_id || await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  // Get current user and validate authentication
  const userId = await getValidatedUserId()
  if (!userId) {
    throw new Error('User authentication required to create client')
  }
  
  // Check client limit restriction before creating
  const { data: restrictionCheck, error: restrictionError } = await supabase.rpc('check_restriction', {
    p_company_id: companyId,
    p_restriction_name: 'clients',
    p_increment_amount: 1,
    p_action_name: 'create_client'
  })
  
  if (restrictionError) {
    logger.error('[supabaseApi] Error checking client restriction:', restrictionError)
    throw new Error('Failed to check client limit')
  }
  
  if (restrictionCheck && restrictionCheck.length > 0 && !restrictionCheck[0].allowed) {
    const restriction = restrictionCheck[0]
    throw new Error(restriction.message || `Client limit reached. You have ${restriction.current_usage} of ${restriction.limit_value} clients. Upgrade to Paid plan for unlimited clients.`)
  }
  
  // Sanitize inputs
  const sanitizedData = {
    ...clientData,
    name: sanitizeInput(clientData.name),
    email: clientData.email ? sanitizeInput(clientData.email) : null,
    phone: clientData.phone ? sanitizeInput(clientData.phone) : null,
    address: clientData.address ? sanitizeInput(clientData.address) : null,
    company_id: companyId,
    created_by: userId
  }
  
  const { data, error } = await supabase
    .from('clients')
    .insert([sanitizedData])
    .select()
    .single()
  
  if (error) throw error
  return data
}

/**
 * Update an existing client
 * Migrated from supabaseApi.js lines 638-658
 */
const _updateClient = async (clientId, clientData) => {
  validateRequired(clientId, 'Client ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  // Sanitize inputs
  const sanitizedData = {
    ...clientData,
    company_id: companyId
  }
  
  if (sanitizedData.name) {
    sanitizedData.name = sanitizeInput(sanitizedData.name)
  }
  if (sanitizedData.email) {
    validateEmail(sanitizedData.email)
    sanitizedData.email = sanitizeInput(sanitizedData.email)
  }
  if (sanitizedData.phone) {
    sanitizedData.phone = sanitizeInput(sanitizedData.phone)
  }
  if (sanitizedData.address) {
    sanitizedData.address = sanitizeInput(sanitizedData.address)
  }
  
  const { data, error } = await supabase
    .from('clients')
    .update(sanitizedData)
    .eq('id', clientId)
    .select()
    .single()
  
  if (error) throw error
  return data
}

/**
 * Delete a client
 * Migrated from supabaseApi.js lines 660-678
 */
const _deleteClient = async (clientId) => {
  validateRequired(clientId, 'Client ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { error } = await supabase
    .from('clients')
    .delete()
    .eq('id', clientId)
  
  if (error) throw error
  return { success: true }
}

/**
 * Get client counts for automations
 * Returns total clients and clients with automation_enabled=true
 */
const _getClientAutomationCounts = async (companyId = null) => {
  logger.supabaseApi('getClientAutomationCounts called with companyId:', companyId)
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    logger.supabaseApi('No company ID for getClientAutomationCounts')
    return createSuccessResponse({ total: 0, automationEnabled: 0 })
  }

  logger.supabaseApi('Fetching client counts for company:', resolvedCompanyId)
  
  // Get total clients count
  const { count: totalCount, error: totalError } = await supabase
    .from('clients')
    .select('*', { count: 'exact', head: true })
    .eq('company_id', resolvedCompanyId)
  
  if (totalError) {
    logger.error('[supabaseApi] Error in getClientAutomationCounts total query:', totalError)
    throw totalError
  }

  // Get clients with automation_enabled=true count
  const { count: enabledCount, error: enabledError } = await supabase
    .from('clients')
    .select('*', { count: 'exact', head: true })
    .eq('company_id', resolvedCompanyId)
    .eq('automation_enabled', true)
  
  if (enabledError) {
    logger.error('[supabaseApi] Error in getClientAutomationCounts enabled query:', enabledError)
    throw enabledError
  }
  
  const result = {
    total: totalCount || 0,
    automationEnabled: enabledCount || 0
  }
  
  logger.supabaseApi('getClientAutomationCounts success:', result)
  return createSuccessResponse(result)
}

// Export wrapped functions with error handling
export const getClients = withErrorHandling(_getClients, 'fetch clients', 'clients')
export const createClient = withErrorHandling(_createClient, 'create client', 'client')
export const updateClient = withErrorHandling(_updateClient, 'update client', 'client')
export const deleteClient = withErrorHandling(_deleteClient, 'delete client', 'client')
export const getClientAutomationCounts = withErrorHandling(_getClientAutomationCounts, 'fetch client automation counts', 'client counts')